// src/pages/expenses/ManageExpensesPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Receipt,
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  DollarSign,
  Calendar,
  CreditCard,
} from 'lucide-react';
import {
  PageHeader,
  StatsCards,
  DataTable,
  FilterPanel,
} from '../../components/common';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import {
  getExpenses,
  deleteExpense,
  getExpenseCategories,
} from '../../services/api/expenses';
import ExpenseModal from './components/ExpenseModal';
import styles from './ManageExpensesPage.module.css';

const PAYMENT_METHOD_KEYS = [
  'cash',
  'bank_transfer',
  'credit_card',
  'cheque',
  'mobile_payment',
  'other',
];

const ManageExpensesPage = () => {
  const { t, formatCurrency, formatDate } = useLocalization();
  const { success, error: showError } = useAlert();
  const { hasPermission } = usePermissions();

  // Data state
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [10, 20, 50, 100],
  });

  // Sorting
  const [sorting, setSorting] = useState({
    orderBy: 'expenseDate',
    orderDirection: 'DESC',
  });

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    categoryId: '',
    paymentMethod: '',
    startDate: '',
    endDate: '',
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedExpense, setSelectedExpense] = useState(null);

  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load categories once
  useEffect(() => {
    loadCategories();
  }, []);

  // Load expenses when filters/pagination/sort change
  useEffect(() => {
    loadExpenses();
  }, [pagination.currentPage, pagination.pageSize, sorting, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCategories = async () => {
    try {
      const data = await getExpenseCategories();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadExpenses = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection,
      };

      if (filters.search) params.search = filters.search;
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.paymentMethod) params.paymentMethod = filters.paymentMethod;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const data = await getExpenses(params);
      setExpenses(data.expenses || []);
      setTotalAmount(data.totalAmount || 0);
      setPagination((prev) => ({ ...prev, total: data.total || 0 }));
    } catch (err) {
      console.error('Error loading expenses:', err);
      showError(err.response?.data?.message || t('expenses.failedToLoadExpenses'));
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, sorting, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newSize) => {
    setPagination((prev) => ({ ...prev, pageSize: newSize, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ search: '', categoryId: '', paymentMethod: '', startDate: '', endDate: '' });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => Object.values(filters).some((v) => v !== '');

  // Category options for filter (flat list of root + children)
  const categoryFilterOptions = categories.flatMap((root) => [
    { value: String(root.id), label: root.name },
    ...(root.children || []).map((child) => ({
      value: String(child.id),
      label: `  ${child.name}`,
    })),
  ]);

  // Handlers
  const handleCreate = () => {
    setModalMode('create');
    setSelectedExpense(null);
    setShowModal(true);
  };

  const handleEdit = (expense) => {
    setModalMode('edit');
    setSelectedExpense(expense);
    setShowModal(true);
  };

  const handleDelete = (expense) => {
    setExpenseToDelete(expense);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!expenseToDelete) return;
    try {
      setIsDeleting(true);
      await deleteExpense(expenseToDelete.id);
      success(t('expenses.expenseDeletedSuccessfully'));
      setShowDeleteDialog(false);
      setExpenseToDelete(null);
      loadExpenses();
    } catch (err) {
      showError(err.response?.data?.message || t('expenses.failedToDeleteExpense'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedExpense(null);
    loadExpenses();
  };

  // Stats cards
  const statsConfig = [
    {
      key: 'total',
      label: t('expenses.totalExpenses'),
      value: pagination.total,
      icon: Receipt,
      color: '#7F5CFF',
      loading: isLoading,
    },
    {
      key: 'amount',
      label: t('expenses.filteredTotal'),
      value: formatCurrency(totalAmount, 'LKR'),
      icon: DollarSign,
      color: '#10b981',
      loading: isLoading,
    },
  ];

  // Filter definitions
  const filterDefinitions = [
    {
      key: 'categoryId',
      label: t('expenses.category'),
      type: 'select',
      placeholder: t('expenses.allCategories'),
      options: categoryFilterOptions,
    },
    {
      key: 'paymentMethod',
      label: t('expenses.paymentMethod'),
      type: 'select',
      placeholder: t('expenses.allPaymentMethods'),
      options: PAYMENT_METHOD_KEYS.map((key) => ({
        value: key,
        label: t(`expenses.paymentMethods.${key}`),
      })),
    },
    {
      key: 'startDate',
      label: t('expenses.dateFrom'),
      type: 'date',
      icon: <Calendar size={18} />,
    },
    {
      key: 'endDate',
      label: t('expenses.dateTo'),
      type: 'date',
      icon: <Calendar size={18} />,
    },
  ];

  // Table columns
  const columns = [
    {
      key: 'expenseDate',
      header: t('expenses.expenseDate'),
      sortable: true,
      width: '120px',
      render: (row) => (
        <div className={styles.dateCell}>{formatDate(row.expenseDate)}</div>
      ),
    },
    {
      key: 'category',
      header: t('expenses.category'),
      width: '180px',
      render: (row) => {
        const cat = row.category;
        if (!cat) return <span className={styles.na}>—</span>;
        const parentName = cat.parent?.name;
        return (
          <div className={styles.categoryCell}>
            {parentName && <span className={styles.parentCategory}>{parentName}</span>}
            <span className={styles.categoryName}>{cat.name}</span>
          </div>
        );
      },
    },
    {
      key: 'title',
      header: t('expenses.title'),
      sortable: true,
      width: '220px',
      render: (row) => <div className={styles.titleCell}>{row.title}</div>,
    },
    {
      key: 'amount',
      header: t('expenses.amount'),
      sortable: true,
      width: '130px',
      render: (row) => (
        <div className={styles.amountCell}>{formatCurrency(row.amount, 'LKR')}</div>
      ),
    },
    {
      key: 'paymentMethod',
      header: t('expenses.paymentMethod'),
      sortable: true,
      width: '150px',
      render: (row) => (
        <div className={styles.paymentMethodCell}>
          <CreditCard size={14} />
          <span>{t(`expenses.paymentMethods.${row.paymentMethod}`)}</span>
        </div>
      ),
    },
    {
      key: 'notes',
      header: t('expenses.notes'),
      width: '180px',
      render: (row) => (
        <div className={styles.notesCell} title={row.notes || ''}>
          {row.notes || <span className={styles.na}>—</span>}
        </div>
      ),
    },
    {
      key: 'createdBy',
      header: t('expenses.createdBy'),
      width: '130px',
      render: (row) => (
        <div className={styles.createdByCell}>
          {row.createdBy?.name || <span className={styles.na}>—</span>}
        </div>
      ),
    },
  ];

  // Table actions
  const tableActions = {
    render: (row) => (
      <div className={styles.actionButtons}>
        {hasPermission('edit_expense') && (
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className={styles.actionButton}
            title={t('common.edit')}
          >
            <Edit size={16} />
          </button>
        )}
        {hasPermission('delete_expense') && (
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
            className={`${styles.actionButton} ${styles.deleteAction}`}
            title={t('common.delete')}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  return (
    <div className={styles.manageExpensesPage}>
      {/* Page Header */}
      <PageHeader
        title={t('expenses.manageExpenses')}
        description={t('expenses.viewAndManageExpenses')}
        icon={Receipt}
        actions={
          hasPermission('create_expense') && (
            <button onClick={handleCreate} className={styles.primaryButton}>
              <Plus size={20} />
              {t('expenses.recordNewExpense')}
            </button>
          )
        }
      />

      {/* Stats */}
      <StatsCards stats={statsConfig} />

      {/* Filters */}
      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('expenses.searchExpenses'),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      {/* Filtered total summary bar */}
      {!isLoading && pagination.total > 0 && (
        <div className={styles.totalSummaryBar}>
          <span>{t('expenses.showingTotal')}:</span>
          <strong>{formatCurrency(totalAmount, 'LKR')}</strong>
          <span className={styles.totalCount}>
            ({pagination.total} {pagination.total === 1 ? 'record' : 'records'})
          </span>
        </div>
      )}

      {/* Data Table */}
      <DataTable
        data={expenses}
        columns={columns}
        actions={(hasPermission('edit_expense') || hasPermission('delete_expense')) ? tableActions : undefined}
        onRowClick={hasPermission('edit_expense') ? (row) => handleEdit(row) : undefined}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters()
            ? t('expenses.noExpensesFound')
            : t('expenses.noExpensesYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('expenses.noExpensesFoundDescription')
            : t('expenses.noExpensesYetDescription')
        }
        emptyIcon={<Receipt size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_expense') ? (
            <button onClick={handleCreate} className={styles.primaryButton}>
              <Plus size={18} />
              {t('expenses.recordNewExpense')}
            </button>
          ) : null
        }
      />

      {/* Expense Modal */}
      {showModal && (
        <ExpenseModal
          mode={modalMode}
          expense={selectedExpense}
          categories={categories}
          onClose={() => { setShowModal(false); setSelectedExpense(null); }}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && expenseToDelete && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <AlertCircle className={styles.dialogIcon} size={24} />
              <h3>{t('expenses.deleteExpense')}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>{t('expenses.confirmDelete')}</p>
              <p className={styles.dialogDescription}>
                <strong>{expenseToDelete.title}</strong> — {formatCurrency(expenseToDelete.amount, 'LKR')}
              </p>
              <p className={styles.dialogWarning}>{t('expenses.confirmDeleteDescription')}</p>
            </div>
            <div className={styles.dialogActions}>
              <button
                onClick={() => { setShowDeleteDialog(false); setExpenseToDelete(null); }}
                className={styles.dialogCancelButton}
                disabled={isDeleting}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                className={styles.dialogDeleteButton}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className={styles.buttonSpinner}></div>
                    {t('common.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    {t('common.delete')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageExpensesPage;
